import {
  Button, Card, ErrorState, Field, Input, LoadingState, Permissions, PhoneInput, Row, Stack, Textarea,
  normalisePhone, useApi, useI18n, usePermission,
} from '@daghfal/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

/**
 * Whom a child is never released to (Q30), kept by the administration.
 *
 * Each entry is shown in red on the staff app's check-out screen, with its note, to whoever records departures.
 * The reference of the decision behind it stays here. Lifting one keeps it on record as lifted.
 */
export function RestrictionsCard({ childId }: { childId: number }) {
  const api = useApi();
  const { t, formatPhone, formatDate } = useI18n();
  const queryClient = useQueryClient();
  const canRead = usePermission(Permissions.attendance.record);
  const canUpdate = usePermission(Permissions.children.update);
  const [adding, setAdding] = useState(false);
  const [showLifted, setShowLifted] = useState(false);

  const key = ['child', childId, 'restrictions', showLifted];
  const restrictions = useQuery({
    queryKey: key,
    queryFn: () => api.pickups.restrictions(childId, showLifted),
    enabled: canRead,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['child', childId, 'restrictions'] });

  const lift = useMutation({
    mutationFn: (restrictionId: number) => api.pickups.liftRestriction(childId, restrictionId),
    onSuccess: () => void refresh(),
  });

  if (!canRead) return null;

  return (
    <Card
      title={t('restrictions.title')}
      hint={t('restrictions.hint')}
      action={canUpdate && !adding
        ? <Button variant="secondary" onClick={() => setAdding(true)}>{t('restrictions.add')}</Button>
        : undefined}
    >
      <Stack>
        {adding && (
          <AddRestrictionForm
            childId={childId}
            onCancel={() => setAdding(false)}
            onAdded={() => { setAdding(false); void refresh(); }}
          />
        )}

        {lift.isError && <ErrorState error={lift.error} />}
        {restrictions.isPending && <LoadingState />}
        {restrictions.isError && <ErrorState error={restrictions.error} onRetry={() => void restrictions.refetch()} />}
        {restrictions.data?.items.length === 0 && <p className="dg-card__hint">{t('restrictions.empty')}</p>}

        {restrictions.data?.items.map((restriction) => (
          <Row key={restriction.id} between>
            <span className="dg-stack dg-stack--tight">
              <strong>{restriction.fullName}</strong>
              <span>{restriction.note}</span>
              {restriction.evidenceRef && (
                <span className="dg-card__hint">{t('restrictions.evidence.shown', { reference: restriction.evidenceRef })}</span>
              )}
              {restriction.liftedAt && (
                <span className="dg-card__hint">{t('restrictions.lifted', { date: formatDate(restriction.liftedAt) })}</span>
              )}
            </span>
            <span className="dg-row">
              {restriction.phone && <a href={`tel:${restriction.phone}`} dir="ltr">{formatPhone(restriction.phone)}</a>}
              {canUpdate && !restriction.liftedAt && (
                <Button
                  variant="ghost"
                  busy={lift.isPending && lift.variables === restriction.id}
                  onClick={() => lift.mutate(restriction.id)}
                >
                  {t('restrictions.lift')}
                </Button>
              )}
            </span>
          </Row>
        ))}

        {canUpdate && (
          <Row>
            <Button variant="ghost" onClick={() => setShowLifted((shown) => !shown)}>
              {showLifted ? t('restrictions.lifted.hide') : t('restrictions.lifted.show')}
            </Button>
          </Row>
        )}
      </Stack>
    </Card>
  );
}

function AddRestrictionForm({ childId, onCancel, onAdded }: {
  childId: number;
  onCancel: () => void;
  onAdded: () => void;
}) {
  const api = useApi();
  const { t } = useI18n();

  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');
  const [evidenceRef, setEvidenceRef] = useState('');
  const [errors, setErrors] = useState<{ fullName?: string; note?: string }>({});

  const add = useMutation({
    mutationFn: () => api.pickups.addRestriction(childId, {
      fullName: fullName.trim(),
      phone: phone.trim() ? normalisePhone(phone) ?? phone.trim() : null,
      note: note.trim(),
      evidenceRef: evidenceRef.trim() || null,
    }),
    onSuccess: onAdded,
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    const next = {
      ...(fullName.trim() ? {} : { fullName: t('restrictions.name.required') }),
      ...(note.trim() ? {} : { note: t('restrictions.note.required') }),
    };
    setErrors(next);
    if (fullName.trim() && note.trim()) add.mutate();
  }

  return (
    <form onSubmit={submit} noValidate>
      <Stack>
        {add.isError && <ErrorState error={add.error} />}

        <Field label={t('restrictions.name')} error={errors.fullName} required>
          {(props) => <Input {...props} value={fullName} onChange={(event) => setFullName(event.target.value)} autoFocus />}
        </Field>

        <Field label={t('auth.phone')}>
          {(props) => <PhoneInput {...props} value={phone} onChange={(event) => setPhone(event.target.value)} />}
        </Field>

        <Field label={t('restrictions.note')} hint={t('restrictions.note.hint')} error={errors.note} required>
          {(props) => <Textarea {...props} value={note} onChange={(event) => setNote(event.target.value)} />}
        </Field>

        <Field label={t('restrictions.evidence')}>
          {(props) => <Input {...props} value={evidenceRef} onChange={(event) => setEvidenceRef(event.target.value)} />}
        </Field>

        <Row>
          <Button type="submit" busy={add.isPending} busyLabel={t('action.saving')}>{t('restrictions.add')}</Button>
          <Button variant="ghost" onClick={onCancel}>{t('action.cancel')}</Button>
        </Row>
      </Stack>
    </form>
  );
}
